import type { HIGHER_OR_EQUAL_CONSTRAINT } from './mustBeHigherOrEqualAt'
import type { LOWER_OR_EQUAL_CONSTRAINT } from './mustBeLowerOrEqualAt'
import type { MAX_LENGTH_CONSTRAINT } from './mustBeMaxLength'
import type { MIN_LENGTH_CONSTRAINT } from './mustBeMinLength'

const i18n = {
  fr: {
    EMAIL_ADDRESS: 'Ce champ doit être une adresse e-mail valide',
    PROFESSIONAL_EMAIL_ADDRESS:
      'Ce champ doit être une adresse e-mail professionnelle',
    HIGHER_OR_EQUAL_TO: (args: HIGHER_OR_EQUAL_CONSTRAINT) =>
      `Ce champ doit être supérieur ou égal à ${args.min}${args?.suffix || ''}`,
    HIGHER_THAN: (args: HIGHER_OR_EQUAL_CONSTRAINT) =>
      `Ce champ doit être supérieur à ${args?.min}${args?.suffix || ''}`,
    LOWER_OR_EQUAL_TO: (args: LOWER_OR_EQUAL_CONSTRAINT) =>
      `Ce champ doit être inférieur ou égal à ${args?.max}${
        args?.suffix || ''
      }`,
    LOWER_THAN: (args: LOWER_OR_EQUAL_CONSTRAINT) =>
      `Ce champ doit être inférieur à ${args?.max}${args?.suffix || ''}`,
    MAX_LENGTH: (args: MAX_LENGTH_CONSTRAINT) =>
      `Ce champ doit avoir ${args?.max} caractères maximum`,
    MIN_LENGTH: (args: MIN_LENGTH_CONSTRAINT) =>
      `Ce champ doit être d'au moins ${args?.min} caractères`,
    NUMBER: 'Ce champ doit être un nombre',
    REQUIRED: 'Ce champ est obligatoire',
    EQUAL_STRING: `Le champ n'est pas identique`, // TODO: refactor by customizing validator to receive JSX
    STRONG_PASSWORD:
      'Le mot de passe doit contenir au moins 8 caractères dont une lettre majuscule, minuscule, un chiffre et un caractère spécial',
    SOFT_PASSWORD:
      'Le mot de passe doit contenir au moins 8 caractères dont une lettre majuscule, une lettre minuscule',
  },
  en: {
    EMAIL_ADDRESS: 'This field must be a valid email',
    PROFESSIONAL_EMAIL_ADDRESS: 'This field must be a professional email',
    HIGHER_OR_EQUAL_TO: (args: HIGHER_OR_EQUAL_CONSTRAINT) =>
      `This field must be higher or equal to ${args?.min}${args?.suffix || ''}`,
    HIGHER_THAN: (args: HIGHER_OR_EQUAL_CONSTRAINT) =>
      `This field must be higher than ${args?.min}${args?.suffix || ''}`,
    LOWER_OR_EQUAL_TO: (args: LOWER_OR_EQUAL_CONSTRAINT) =>
      `This field must be lower or equal to ${args?.max}${args?.suffix || ''}`,
    LOWER_THAN: (args: LOWER_OR_EQUAL_CONSTRAINT) =>
      `This field must be lower than ${args?.max}${args?.suffix || ''}`,
    MAX_LENGTH: (args: MAX_LENGTH_CONSTRAINT) =>
      `This field must have max ${args?.max} characteres`,
    MIN_LENGTH: (args: MIN_LENGTH_CONSTRAINT) =>
      `This field must have at least ${args?.min} characteres`,
    NUMBER: 'This field must be a number',
    REQUIRED: 'This field is required',
    EQUAL_STRING: 'The field is not the same',
    STRONG_PASSWORD:
      'The password must contain at least 8 characters, including an upper case letter, a lower case letter, a number and a special character',
    SOFT_PASSWORD:
      'The password must contain at least 8 characters, including an uppercase letter, a lowercase letter',
  },
  'pt-br': {
    EMAIL_ADDRESS: 'Esse campo deve ser um e-mail válido',
    PROFESSIONAL_EMAIL_ADDRESS: 'Esse campo deve ser um e-mail profissional',
    HIGHER_OR_EQUAL_TO: (args: HIGHER_OR_EQUAL_CONSTRAINT) =>
      `Esse campo deve ser maior ou igual a  ${args?.min}${args?.suffix || ''}`,
    HIGHER_THAN: (args: HIGHER_OR_EQUAL_CONSTRAINT) =>
      `Esse campo deve ser maior que ${args?.min}${args?.suffix || ''}`,
    LOWER_OR_EQUAL_TO: (args: LOWER_OR_EQUAL_CONSTRAINT) =>
      `Esse campo deve ser menor ou igual a ${args?.max}${args?.suffix || ''}`,
    LOWER_THAN: (args: LOWER_OR_EQUAL_CONSTRAINT) =>
      `Esse campo deve ser menor que ${args?.max}${args?.suffix || ''}`,
    MAX_LENGTH: (args: MAX_LENGTH_CONSTRAINT) =>
      `Esse campo deve ter no máximo ${args?.max} caracteres`,
    MIN_LENGTH: (args: MIN_LENGTH_CONSTRAINT) =>
      `Esse campo deve ter pelo menos ${args?.min} caracteres`,
    NUMBER: 'Este campo deve ser um número',
    REQUIRED: 'Este campo é obrigatório',
    EQUAL_STRING: 'Este campo não é o mesmo',
    STRONG_PASSWORD:
      'A senha deve conter pelo menos 8 caracteres, incluindo uma letra maiúscula, uma letra minúscula, um número e um caractere especial',
    SOFT_PASSWORD:
      'A senha deve conter pelo menos 8 caracteres, incluindo uma letra maiúscula, uma letra minúscula',
  },
  es: {
    EMAIL_ADDRESS: 'Este campo debe ser un e-mail válido',
    PROFESSIONAL_EMAIL_ADDRESS: 'Este campo debe ser un e-mail profesional',
    HIGHER_OR_EQUAL_TO: (args: HIGHER_OR_EQUAL_CONSTRAINT) =>
      `Este campo debe ser mayor o igual que ${args?.min}${args?.suffix || ''}`,
    HIGHER_THAN: (args: HIGHER_OR_EQUAL_CONSTRAINT) =>
      `Este campo debe ser superior a ${args?.min}${args?.suffix || ''}`,
    LOWER_OR_EQUAL_TO: (args: LOWER_OR_EQUAL_CONSTRAINT) =>
      `Este campo debe ser inferior o igual a ${args?.max}${
        args?.suffix || ''
      }`,
    LOWER_THAN: (args: LOWER_OR_EQUAL_CONSTRAINT) =>
      `Este campo debe ser inferior a ${args?.max}${args?.suffix || ''}`,
    MAX_LENGTH: (args: MAX_LENGTH_CONSTRAINT) =>
      `Este campo debe tener como máximo ${args?.max} caracteres`,
    MIN_LENGTH: (args: MIN_LENGTH_CONSTRAINT) =>
      `Este campo debe tener al menos ${args?.min} caracteres`,
    NUMBER: 'Este campo debe ser un número',
    REQUIRED: 'Este campo es obligatorio',
    EQUAL_STRING: 'El campo no es el mismo',
    CONTRASEÑA_FUERTE:
      'La contraseña debe contener al menos 8 caracteres, incluyendo una letra mayúscula, una letra minúscula, un número y un carácter especial',
    SOFT_PASSWORD:
      'La contraseña debe contener al menos 8 caracteres, incluyendo una letra mayúscula, una letra minúscula',
  },
  de: {
    EMAIL_ADDRESS: 'Dieses Feld muss eine gültige E-Mail sein',
    PROFESSIONAL_EMAIL_ADDRESS:
      'Dieses Feld muss eine professionelle E-Mail sein',
    HIGHER_OR_EQUAL_TO: (args: HIGHER_OR_EQUAL_CONSTRAINT) =>
      `Dieses Feld muss höher oder gleich ${args?.min}${
        args?.suffix || ''
      } sein`,
    HIGHER_THAN: (args: HIGHER_OR_EQUAL_CONSTRAINT) =>
      `Dieses Feld muss höher als ${args?.min}${args?.suffix || ''} sein`,
    LOWER_OR_EQUAL_TO: (args: LOWER_OR_EQUAL_CONSTRAINT) =>
      `Dieses Feld muss kleiner oder gleich ${args?.max}${
        args?.suffix || ''
      } sein`,
    LOWER_THAN: (args: LOWER_OR_EQUAL_CONSTRAINT) =>
      `Dieses Feld muss kleiner als ${args?.max}${args?.suffix || ''} sein`,
    MAX_LENGTH: (args: MAX_LENGTH_CONSTRAINT) =>
      `Dieses Feld darf maximal ${args?.max} Zeichen enthalten`,
    MIN_LENGTH: (args: MIN_LENGTH_CONSTRAINT) =>
      `Dieses Feld muss mindestens ${args?.min} Zeichen enthalten`,
    NUMBER: 'Dieses Feld muss eine Zahl sein',
    REQUIRED: 'Dieses Feld ist erforderlich',
    EQUAL_STRING: 'Das Feld ist nicht identisch',
    STRONG_PASSWORD: '', // TODO: refactor by customizing validator to receive JSX
  },
}

export default i18n
